import { useEffect, useRef, useState } from 'react';
import {
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import type { MapArea } from '../types/MapArea.types';
import type { UseCleaningAreaListDndResult } from '../types/CleaningAreaList.types';

/**
 * Drag-and-drop + ordering state for CleaningAreaList: selected areas are draggable to reorder,
 * unselected areas render below them (not draggable). `orderedAreas` is selected-then-unselected,
 * matching what's rendered; `getDropIndicatorFor` tells a row whether to show the line above/below
 * it while a drag is in progress.
 */
export const useCleaningAreaListDnd = (
  areas: MapArea[],
  selectedAreaIds: string[],
  onReorder: (nextAreaIds: string[]) => void
): UseCleaningAreaListDndResult => {
  const sensors = useSensors(useSensor(PointerSensor));
  const [activeId, setActiveId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const firstRowRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!containerRef.current || !firstRowRef.current) {
      return;
    }
    // Scroll past the top spacer by exactly however much space it (plus the flex gap) actually
    // takes up, rather than assuming a fixed px value — measured, so it can't drift out of sync
    // with the spacer/gap CSS. This is what keeps the list flush with the map on open while still
    // leaving the spacer reachable (manually, or via dnd-kit's auto-scroll) for the drop indicator.
    const offset = firstRowRef.current.getBoundingClientRect().top - containerRef.current.getBoundingClientRect().top;
    containerRef.current.scrollTop += offset;
  }, []);

  const selectedAreas = selectedAreaIds
    .map(areaId => areas.find(area => area.id === areaId))
    .filter((area): area is MapArea => Boolean(area));
  const unselectedAreas = areas.filter(area => !selectedAreaIds.includes(area.id));
  const orderedAreas = [...selectedAreas, ...unselectedAreas];

  const activeIndex = activeId ? selectedAreas.findIndex(area => area.id === activeId) : -1;
  const overIndex = overId ? selectedAreas.findIndex(area => area.id === overId) : -1;
  const activeArea = activeId ? selectedAreas.find(area => area.id === activeId) : undefined;

  const getDropIndicatorFor = (areaId: string): 'top' | 'bottom' | null => {
    const isOverTarget = areaId === overId && activeId !== null && activeId !== overId;
    if (isOverTarget && overIndex !== -1) {
      return activeIndex > overIndex ? 'top' : 'bottom';
    }

    // Dragging a selected item past the end of the selected group (over an unselected area) —
    // clamp the indicator to the boundary instead of following it into the unselected rows,
    // since a reorder can only land within the selected group.
    if (
      activeId !== null &&
      overId !== null &&
      overIndex === -1 &&
      areaId === selectedAreas[selectedAreas.length - 1]?.id
    ) {
      return 'bottom';
    }

    return null;
  };

  const handleDragStart = (event: DragStartEvent): void => {
    setActiveId(event.active.id as string);
  };

  const handleDragOver = (event: DragOverEvent): void => {
    setOverId((event.over?.id as string) ?? null);
  };

  const handleDragEnd = (event: DragEndEvent): void => {
    const { active, over } = event;
    setActiveId(null);
    setOverId(null);

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = selectedAreas.findIndex(area => area.id === active.id);
    if (oldIndex === -1) {
      return;
    }

    // Dropping on an unselected area (past the end of the selected group) moves the item to the
    // last selected position, matching the boundary drop indicator shown in that case.
    const overIndexInSelected = selectedAreas.findIndex(area => area.id === over.id);
    const newIndex = overIndexInSelected === -1 ? selectedAreas.length - 1 : overIndexInSelected;

    onReorder(arrayMove(selectedAreas, oldIndex, newIndex).map(area => area.id));
  };

  const handleDragCancel = (): void => {
    setActiveId(null);
    setOverId(null);
  };

  return {
    sensors,
    orderedAreas,
    activeArea,
    activeIndex,
    containerRef,
    firstRowRef,
    isAnyDragging: activeId !== null,
    getDropIndicatorFor,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    handleDragCancel,
  };
};
